/**
 * Activity Types for Turn Connect
 * Shared between web and mobile platforms
 *
 * Activities represent audit trails of user actions and system events.
 * They support filtering by category, user, date range, and resource.
 *
 * Note: The base Activity and ListActivitiesResponse types are defined in workOrder.ts
 * This file extends those types with additional functionality for the Activity Log feature.
 */
/**
 * Available activity categories with display metadata
 */
export const ACTIVITY_CATEGORIES = [
    {
        value: 'financial',
        label: 'Financial',
        color: '#22C55E',
        description: 'Service approvals, pricing changes, labor modifications',
    },
    {
        value: 'workflow',
        label: 'Workflow',
        color: '#3B82F6',
        description: 'State transitions, department changes, work order flow',
    },
    {
        value: 'service_management',
        label: 'Service Management',
        color: '#8B5CF6',
        description: 'Adding/removing services, service updates',
    },
    {
        value: 'user_action',
        label: 'User Action',
        color: '#F59E0B',
        description: 'Notes, file attachments, assignments',
    },
    {
        value: 'administration',
        label: 'Administration',
        color: '#EF4444',
        description: 'User management, role assignments, settings',
    },
];
/**
 * Get category info by value
 */
export function getCategoryInfo(category) {
    return ACTIVITY_CATEGORIES.find((c) => c.value === category);
}
/**
 * Get category color by value
 */
export function getCategoryColor(category) {
    return getCategoryInfo(category)?.color || '#9CA3AF';
}
/**
 * Get date range values for a preset
 */
export function getDateRangeFromPreset(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
        case 'today':
            return {
                from: today,
                to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
            };
        case 'yesterday': {
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            return {
                from: yesterday,
                to: new Date(yesterday.getTime() + 24 * 60 * 60 * 1000 - 1),
            };
        }
        case 'last_7_days':
            return {
                from: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
                to: now,
            };
        case 'last_30_days':
            return {
                from: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
                to: now,
            };
        case 'this_month':
            return {
                from: new Date(now.getFullYear(), now.getMonth(), 1),
                to: now,
            };
        case 'last_month': {
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
            return {
                from: lastMonth,
                to: lastMonthEnd,
            };
        }
        case 'custom':
            return null;
        default:
            return null;
    }
}
/**
 * Format an activity type for display
 * e.g., 'work_order.service_approved' -> 'Service Approved'
 */
/**
 * Activity types whose slug does not read as words when mechanically
 * title-cased ("invoice_delivery_list.updated" → "Invoice Delivery List.Updated").
 * Labels use the names staff see on screen.
 */
const ACTIVITY_TYPE_LABELS = {
    'invoice_rule.created': 'Invoice schedule created',
    'invoice_rule.updated': 'Invoice schedule changed',
    'invoice_rule.deleted': 'Invoice schedule deleted',
    'invoice_delivery_list.created': 'Delivery list created',
    'invoice_delivery_list.updated': 'Delivery list changed',
    'invoice_delivery_list.deleted': 'Delivery list deleted',
};
/** The explicit label for an activity type, when it has one. */
export function activityTypeLabel(activityType) {
    return ACTIVITY_TYPE_LABELS[activityType];
}
export function formatActivityType(activityType) {
    const label = activityTypeLabel(activityType);
    if (label)
        return label;
    return activityType
        .replace('work_order.', '')
        .replace('work_order_service.', 'Service ')
        .replace('work_order_department.', 'Department ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
}
/**
 * Get icon name for activity type
 */
export function getActivityTypeIcon(activityType) {
    // Pricing adjustments: percent badge for applies, keep trash for removes.
    if (activityType.startsWith('work_order_adjustment.')) {
        return activityType.includes('removed') ? 'trash-2' : 'badge-percent';
    }
    if (activityType.startsWith('adjustment_rule.'))
        return 'badge-percent';
    // MUST precede the 'completed' test: "unCOMPLETED" contains "completed", so
    // an uncomplete would otherwise get the success check-mark for a reversal.
    if (activityType.includes('uncompleted'))
        return 'rotate-ccw';
    // Sent back for approval — a reversal, not an approval.
    if (activityType.includes('reapproval'))
        return 'rotate-ccw';
    if (activityType.includes('created'))
        return 'plus-circle';
    if (activityType.includes('completed'))
        return 'check-circle';
    if (activityType.includes('approved'))
        return 'check-circle-2';
    if (activityType.includes('denied'))
        return 'x-circle';
    if (activityType.includes('updated') || activityType.includes('changed'))
        return 'edit';
    if (activityType.includes('added'))
        return 'plus';
    if (activityType.includes('removed') || activityType.includes('deleted'))
        return 'trash-2';
    if (activityType.includes('assigned'))
        return 'user-plus';
    if (activityType.includes('unassigned'))
        return 'user-minus';
    if (activityType.includes('moved') || activityType.includes('transition'))
        return 'arrow-right';
    if (activityType.includes('note'))
        return 'message-square';
    if (activityType.includes('file') || activityType.includes('photo'))
        return 'paperclip';
    return 'activity';
}
/**
 * Get color for activity type
 */
export function getActivityTypeColor(activityType) {
    // Applied adjustments are money events (green); removals fall through to
    // the shared removed/deleted red below.
    if (activityType === 'work_order_adjustment.applied')
        return '#22C55E';
    // MUST precede the 'completed' test: "unCOMPLETED" contains "completed", so
    // an uncomplete would otherwise paint green — the success colour — for what
    // is a reversal. Amber matches the other "changed" events.
    if (activityType.includes('uncompleted'))
        return '#F59E0B';
    // Sent back for approval — amber like the other reversals.
    if (activityType.includes('reapproval'))
        return '#F59E0B';
    if (activityType.includes('created'))
        return '#22C55E';
    if (activityType.includes('completed'))
        return '#22C55E';
    if (activityType.includes('approved'))
        return '#3B82F6';
    if (activityType.includes('denied'))
        return '#EF4444';
    if (activityType.includes('updated') || activityType.includes('changed'))
        return '#F59E0B';
    if (activityType.includes('added'))
        return '#8B5CF6';
    if (activityType.includes('removed') || activityType.includes('deleted'))
        return '#EF4444';
    if (activityType.includes('assigned'))
        return '#06B6D4';
    return '#9CA3AF';
}
/**
 * Parse changes from activity metadata
 * Handles multiple formats:
 * - Array format: changes: [{field, old_value, new_value}, ...]
 * - Object format: changes: {field: {old: x, new: y}, ...}
 * - Legacy format: old_values and new_values objects
 */
export function parseActivityChanges(metadata) {
    if (!metadata)
        return [];
    const typedMetadata = metadata;
    // Format 1: changes array
    if (typedMetadata.changes && Array.isArray(typedMetadata.changes)) {
        return typedMetadata.changes;
    }
    // Format 2: changes object with {field: {old: x, new: y}} structure
    if (typedMetadata.changes && typeof typedMetadata.changes === 'object' && !Array.isArray(typedMetadata.changes)) {
        const changesObj = typedMetadata.changes;
        const changes = [];
        Object.entries(changesObj).forEach(([key, value]) => {
            // Check if value is an {old, new} object
            if (value && typeof value === 'object' && 'old' in value && 'new' in value) {
                const changeValue = value;
                changes.push({
                    field: key,
                    label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                    old_value: changeValue.old,
                    new_value: changeValue.new,
                });
            }
            else {
                // Fallback: treat as new value only
                changes.push({
                    field: key,
                    label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                    old_value: null,
                    new_value: value,
                });
            }
        });
        return changes;
    }
    // Format 3: Legacy format with old_values and new_values objects
    if (typedMetadata.old_values || typedMetadata.new_values) {
        const changes = [];
        const allKeys = new Set([...Object.keys(typedMetadata.old_values || {}), ...Object.keys(typedMetadata.new_values || {})]);
        allKeys.forEach((key) => {
            const oldVal = typedMetadata.old_values?.[key];
            const newVal = typedMetadata.new_values?.[key];
            if (oldVal !== newVal) {
                changes.push({
                    field: key,
                    label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                    old_value: oldVal,
                    new_value: newVal,
                });
            }
        });
        return changes;
    }
    return [];
}
/**
 * Format a change value for display
 */
export function formatChangeValue(value, type) {
    if (value === null || value === undefined)
        return '-';
    if (value === '')
        return '(empty)';
    switch (type) {
        case 'currency':
            return typeof value === 'number' ? `$${value.toFixed(2)}` : String(value);
        case 'duration':
            return typeof value === 'number' ? `${value} hrs` : String(value);
        case 'boolean':
            return value ? 'Yes' : 'No';
        case 'date':
            if (typeof value === 'string') {
                const date = new Date(value);
                return date.toLocaleDateString();
            }
            return String(value);
        default:
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }
            return String(value);
    }
}
